import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CacheService } from '@ukam/core/services/cache.service';
import { ResultService } from '@ukam/core/services/result.service';
import { StoredFile, createStoredFile } from '@ukam/core/storages/models/stored-file';
import { StoredFileApiService } from '@ukam/core/storages/services/stored-file-api.service';
import { StoredFileFinderSearchService } from './stored-file-finder-search.service';

/** Arquivo selecionado no localizador. A tela é de consulta: não cria nem edita conteúdo. */
@Injectable({ providedIn: 'root' })
export class StoredFileFinderService {
  private apiService = inject(StoredFileApiService);
  private searchService = inject(StoredFileFinderSearchService);
  private resultService = inject(ResultService);
  private translocoService = inject(TranslocoService);
  private cacheService = inject(CacheService);

  working = signal(false);
  model = signal<StoredFile | null>(null);

  constructor() {
    this.cacheService.register(() => this.clear());
  }

  getById(id: string): void {
    this.working.set(true);
    this.apiService.getById(id).subscribe({
      next: (result) => {
        this.model.set(createStoredFile(result));
        this.working.set(false);
      },
      error: (error) => {
        this.resultService.showError(error);
        this.working.set(false);
      },
    });
  }

  canDownload(file: StoredFile | null): boolean {
    return file?.status?.key === 'available' || file?.status?.key === 'superseded';
  }

  /** URL pública quando houver; senão gera a URL assinada e abre em outra aba. */
  download(file: StoredFile | null): void {
    if (!file) return;

    if (file.publicUrl) {
      window.open(file.publicUrl, '_blank', 'noopener');
      return;
    }

    if (!file.id || !this.canDownload(file)) return;

    this.working.set(true);
    this.apiService.getDownloadUrl(file.id).subscribe({
      next: (result) => {
        window.open(result.url, '_blank', 'noopener');
        this.working.set(false);
      },
      error: (error) => {
        this.resultService.showError(error);
        this.working.set(false);
      },
    });
  }

  async copyPublicUrl(file: StoredFile | null): Promise<void> {
    if (!file?.publicUrl) return;

    try {
      await navigator.clipboard.writeText(file.publicUrl);
      this.resultService.showSuccess(this.translocoService.translate('URL copied to clipboard'));
    } catch (error) {
      this.resultService.showError(error);
    }
  }

  reload(): void {
    this.searchService.search().subscribe();
  }

  clear(): void {
    this.model.set(null);
  }
}
